/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { ErrorMarker, DeleteIcon } from 'assets/icons';
import { subdirectory } from 'utils';
import PropTypes from 'prop-types';
import * as S from './Upload.styled';
import { useDispatch, useSelector } from 'react-redux';
import { creativesActions } from 'store/campaign/creatives';
import { Content } from './Content';
export const sizes = {
  Big: 'Big',
  Medium: 'Medium',
  Normal: 'Normal',
};

const Upload = React.memo(
  ({
    id,
    name,
    value,
    size,
    accept = 'image/*',
    error = false,
    errorText,
    maxWidth,
    maxHeight,
    onChange,
    onError,
    containerStyle = {},
    uploadWrapperStyle = {},
    buttonStyle = {},
    buttonText = 'UPLOAD',
    environment = 'manageApps',
  }) => {
    const [file, setFile] = useState(null);

    let link =
      value && typeof value === 'string'
        ? `https://cdn.appnext.com/${subdirectory(value)}/${value}`
        : null;

    if (environment === 'creative' || environment === 'accountSettings') {
      link = value;
    }

    const [preview, setPreview] = useState(link);

    useEffect(() => {
      setPreview(link);
    }, [link]);

    const dispatch = useDispatch();

    const loadImg = async (selectedFile) => {
      const img = new Image();
      img.src = URL.createObjectURL(selectedFile);
      const res = await getImage(img.src)
        .then(function () {
          if (maxWidth) {
            if ((maxWidth && img.width !== maxWidth) || (maxHeight && img.height !== maxHeight)) {
              const msg = `The image size is invalid. Please upload an image no larger than ${maxWidth} x ${maxHeight} pixels`;
              onError ? onError(msg) : alert(msg);
              return false;
            }
          }

          if (accept.startsWith('image/') && accept.replace('image/', '') !== '*') {
            const fileFormats = accept
              .replaceAll(' ', '')
              .split(',')
              .map((item) => item.split('/').at(-1));

            if (
              fileFormats &&
              !fileFormats.some((format) => selectedFile.name.toLowerCase().endsWith(format))
            ) {
              throw new Error('invalid format');
            }
          }

          setFile(selectedFile);
          setPreview(URL.createObjectURL(selectedFile));

          return true;
        })
        .catch(function (err) {
          const msg = 'Unsupported file type. Please upload an image file (e.g., JPG, PNG)';
          onError ? onError(msg) : alert(msg);
        });
      return res;
    };

    function getImage(url) {
      return new Promise(function (resolve, reject) {
        const img = new Image();
        img.onload = function () {
          resolve(url);
        };
        img.onerror = function () {
          reject(url);
        };
        img.src = url;
      });
    }

    function getPdf(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader(file);
        reader.onload = (e) => {
          const bytes = new Uint8Array(e.target.result);
          const header = new TextDecoder().decode(bytes.subarray(0, 5));

          if (header.startsWith('%PDF-')) {
            resolve(file);
          } else {
            reject(new Error('File is not a valid PDF'));
          }
        };

        reader.onerror = () => reject(new Error('Failed to read PDF file'));
        reader.readAsArrayBuffer(file);
      });
    }

    const isFileTypeAllowed = (file) => {
      const acceptedTypes = accept.split(',').map((t) => t.trim().toLowerCase());

      // Check MIME type match (e.g. "image/png", "application/pdf")
      if (acceptedTypes.includes(file.type.toLowerCase())) return true;

      // Check wildcard match (e.g. "image/*")
      const [typeGroup] = file.type.toLowerCase().split('/');
      if (acceptedTypes.includes(`${typeGroup}/*`)) return true;

      // Check by extension against accepted MIME subtypes (e.g. "image/jpeg" allows ".jpeg")
      const ext = file.name.toLowerCase().split('.').pop();
      return acceptedTypes.some((t) => t.split('/').pop() === ext);
    };

    const handleChange = async (e) => {
      const currentFile = e.target.files[0];
      if (!currentFile) return;

      e.target.value = null;

      if (!isFileTypeAllowed(currentFile)) {
        const msg = 'Unsupported file type. Please upload an image file (e.g., JPG, PNG)';
        onError ? onError(msg) : alert(msg);
        return;
      }

      if (currentFile.type === 'application/pdf') {
        await getPdf(currentFile);
        const blobUrl = URL.createObjectURL(currentFile);
        setFile(currentFile);
        setPreview(blobUrl);
        onChange(currentFile);
        return;
      }

      const res = await loadImg(currentFile);
      if (res) {
        onChange(currentFile);
      }
    };

    const deleteFile = () => {
      setFile(null);
      setPreview(null);
      onChange(null);
    };
    // const app_icon = useSelector((state) => state.creatives.creativeAndTrackingData.app_icon);
    // const app_icon_selected_creative = useSelector(
    //   (state) => state.creatives.selectedCreative?.icon,
    // );

    return (
      <S.Container id={id} name={name} size={size} style={containerStyle}>
        <S.UploadWrapper isEmpty={!file} error={error} size={size} style={uploadWrapperStyle}>
          <Content
            file={file}
            preview={preview}
            // app_icon={app_icon}
            deleteFile={deleteFile}
            buttonStyle={buttonStyle}
            buttonText={buttonText}
            handleChange={handleChange}
            accept={accept}
            sizes={sizes}
          />
        </S.UploadWrapper>
        {error && (
          <S.ErrorWrapper>
            <ErrorMarker />
            <S.ErrorText>{errorText}</S.ErrorText>
          </S.ErrorWrapper>
        )}
      </S.Container>
    );
  },
);

Upload.Size = {
  ...sizes,
};

Upload.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  size: PropTypes.string.isRequired,
  accept: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  defaultLink: PropTypes.string,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  limit: PropTypes.number,
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  containerStyle: PropTypes.object,
  uploadWrapperStyle: PropTypes.object,
  buttonStyle: PropTypes.object,
  buttonText: PropTypes.string,
};

Upload.displayName = 'Upload';

export default Upload;
